import { useEffect, useMemo, useState } from 'react';
import { X, ScrollText, Search, Copy, Check, Server, Cpu, AlertTriangle, ExternalLink, FileJson, FileText, ChevronDown } from 'lucide-react';
import { getDescriptions } from '../lib/descriptions';

// ── Config ───────────────────────────────────────────────────────────

const STAGE_CONFIG = {
    INIT: { icon: '🚀', label: 'Initialize' },
    NM_CHECK: { icon: '🏷️', label: 'Noun/Modifier Resolution' },
    DB_CHECK: { icon: '🗃️', label: 'Master DB Check' },
    SEARCH: { icon: '🔍', label: 'Supplier-Validated Search' },
    AGENT: { icon: '🕵️', label: 'Agentic URL Recovery' },
    AI_SELECT: { icon: '🤖', label: 'AI Link Ranking' },
    MFR_VALIDATE: { icon: '🏭', label: 'Manufacturer Validation' },
    MFR_FALLBACK: { icon: '🔄', label: 'Manufacturer Fallback' },
    SCRAPE: { icon: '📄', label: 'Web Scrape' },
    SPEC_EXTRACT: { icon: '🧹', label: 'Spec Extraction' },
    NM_CLEAN: { icon: '🧼', label: 'NM Value Cleanup' },
    PROMPT: { icon: '📝', label: 'Prompt Build' },
    DESC_AI: { icon: '🤖', label: 'Description AI (Fallback)' },
    EXTRACT: { icon: '⚙️', label: 'AI Extraction' },
    CUSTOM_CLEAN: { icon: '🧼', label: 'Custom Code: PN & N/M Cleanup' },
    CONDITION_MEMORY: { icon: '🧠', label: 'Step 6 — Missing Property Conditions' },
    HOLD: { icon: '⏸️', label: 'Hold' },
    COMPLETE: { icon: '✅', label: 'Complete' },
    API: { icon: '🔌', label: 'API Call' },
};

const RESULT_DOT = {
    success: 'bg-emerald-500', match: 'bg-blue-500', info: 'bg-slate-400', warning: 'bg-amber-500',
    skipped: 'bg-amber-500', hold: 'bg-orange-500', pending: 'bg-purple-500', error: 'bg-red-500',
};

const RESULT_BADGE = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200', match: 'bg-blue-50 text-blue-700 border-blue-200',
    info: 'bg-slate-50 text-slate-700 border-slate-200', warning: 'bg-amber-50 text-amber-700 border-amber-200',
    skipped: 'bg-amber-50 text-amber-700 border-amber-200', hold: 'bg-orange-50 text-orange-700 border-orange-200',
    pending: 'bg-purple-50 text-purple-700 border-purple-200', error: 'bg-red-50 text-red-700 border-red-200',
};

const STATUS_COLORS = {
    PENDING: 'bg-amber-100 text-amber-800', NM_PENDING: 'bg-rose-100 text-rose-800', BATCH_PENDING: 'bg-purple-100 text-purple-800',
    REVIEW: 'bg-sky-100 text-sky-800', HOLD: 'bg-orange-100 text-orange-800', COMPLETED: 'bg-emerald-100 text-emerald-800',
    PROCESSING: 'bg-blue-100 text-blue-800',
    NO_MATCH: 'bg-red-100 text-red-800',
};

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'steps', label: 'Steps' },
    { key: 'api', label: 'API calls' },
    { key: 'errors', label: 'Errors' },
];

// ── Helpers ──────────────────────────────────────────────────────────

const stageOf = (entry) => STAGE_CONFIG[entry.stage] || { icon: '📌', label: entry.stage || 'Entry' };

const formatTime = (ts) => ts
    ? new Date(ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';

const formatMs = (ms) => (ms === undefined || ms === null) ? '' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`;

const toText = (value) => typeof value === 'string' ? value : JSON.stringify(value, null, 2);

const joinTexts = (parts) => (parts || []).map(p => (typeof p === 'string' ? p : p?.text || '')).filter(Boolean).join('\n');

/**
 * Pull the human-readable prompt / output out of a raw external call,
 * for the AI providers and services the pipeline uses.
 */
function describeExternalCall(call) {
    const req = call.request && typeof call.request === 'object' ? call.request : {};
    const res = call.response && typeof call.response === 'object' ? call.response : {};
    const out = { model: null, prompt: null, output: null, usage: null };

    if (call.service === 'Gemini' || Array.isArray(req.contents)) {
        out.model = call.model || (call.url.match(/models\/([^:/?]+)/) || [])[1] || null;
        const system = joinTexts(req.systemInstruction?.parts || req.system_instruction?.parts);
        const contents = (req.contents || []).map(c => joinTexts(c.parts)).filter(Boolean).join('\n\n');
        out.prompt = [system && `[system]\n${system}`, contents].filter(Boolean).join('\n\n') || null;
        out.output = (res.candidates || []).map(c => joinTexts(c.content?.parts)).filter(Boolean).join('\n\n') || null;
        out.usage = res.usageMetadata || null;
        return out;
    }
    if (Array.isArray(req.messages) || req.input !== undefined) {
        out.model = call.model || req.model || res.model || null;
        if (Array.isArray(req.messages)) {
            out.prompt = req.messages.map(m => {
                const content = Array.isArray(m.content) ? joinTexts(m.content) : m.content;
                return `[${m.role}]\n${content ?? ''}`;
            }).join('\n\n');
        } else {
            out.prompt = typeof req.input === 'string' ? req.input : toText(req.input);
        }
        out.output = res.choices?.[0]?.message?.content
            || res.output_text
            || (res.output || []).flatMap(o => o.content || []).map(c => c.text).filter(Boolean).join('\n')
            || null;
        out.usage = res.usage || null;
        return out;
    }
    if (call.service === 'Serper Search' || req.q) {
        out.prompt = req.q ? `Search query: ${req.q}` : null;
        return out;
    }
    if (call.service === 'Firecrawl') {
        out.prompt = req.url ? `Scrape URL: ${req.url}` : null;
        out.output = res.data?.markdown || res.markdown || null;
    }
    return out;
}

const formatCaller = (c) => c ? `${c.file}:${c.line} → ${c.function}()` : '';

/** Short "Service · model" summary of the external calls behind one API call */
const summarizeModels = (calls) => [...new Set((calls || []).map(c => {
    const model = c.model || describeExternalCall(c).model;
    return model ? `${c.service} · ${model}` : c.service;
}))].join(', ');

const entryMatches = (entry, query) => {
    if (!query) return true;
    try { return JSON.stringify(entry).toLowerCase().includes(query); } catch { return false; }
};

// ── Small UI pieces ──────────────────────────────────────────────────

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);
    return (
        <button
            onClick={() => {
                navigator.clipboard.writeText(text || '').then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                }).catch(() => {});
            }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold bg-white text-slate-600 hover:bg-slate-100 border border-slate-200"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
};

/** Collapsible block showing text or JSON with a copy button */
const ContentBlock = ({ title, value, defaultOpen = true, tone = 'dark' }) => {
    if (value === null || value === undefined || value === '') return null;
    const text = toText(value);
    const pre = tone === 'dark'
        ? 'bg-slate-900 text-slate-100 border-slate-700'
        : 'bg-slate-50 text-slate-800 border-slate-200';
    return (
        <details open={defaultOpen} className="group rounded-xl border border-slate-200 bg-white">
            <summary className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none">
                <ChevronDown className="w-4 h-4 text-slate-400 -rotate-90 group-open:rotate-0 transition-transform" />
                <span className="text-xs font-bold text-slate-700 uppercase tracking-wide flex-1">{title}</span>
                <span className="text-[10px] font-mono text-slate-400">{text.length.toLocaleString()} chars</span>
                <span onClick={(e) => e.preventDefault()}><CopyButton text={text} /></span>
            </summary>
            <pre className={`mx-3 mb-3 p-3 rounded-lg border text-[12px] leading-relaxed font-mono whitespace-pre-wrap break-words max-h-[60vh] overflow-auto ${pre}`}>
                {text}
            </pre>
        </details>
    );
};

const InfoRow = ({ label, value, mono, link }) => {
    if (!value && value !== 0) return null;
    return (
        <div className="flex items-start gap-3 py-1.5 border-b border-slate-100 last:border-0">
            <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide w-[140px] shrink-0 pt-0.5">{label}</span>
            <span className={`text-sm text-slate-800 break-all flex-1 ${mono ? 'font-mono' : ''}`}>
                {link
                    ? <a href={value} target="_blank" rel="noopener noreferrer" className="text-indigo-600 underline hover:text-indigo-800 inline-flex items-center gap-1">{value} <ExternalLink className="w-3 h-3 shrink-0" /></a>
                    : String(value)}
            </span>
        </div>
    );
};

const Card = ({ title, children }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">{title}</h4>
        {children}
    </div>
);

// ── Detail panes ─────────────────────────────────────────────────────

/** Render an entry's data: short values as rows, long text and objects as blocks */
const DataView = ({ data }) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        return <p className="text-sm text-slate-400">No data recorded for this step.</p>;
    }
    const rows = [];
    const blocks = [];
    for (const [key, value] of Object.entries(data)) {
        if (value === null || value === undefined || value === '') continue;
        const isShort = typeof value !== 'object' && String(value).length <= 160 && !String(value).includes('\n');
        if (isShort) rows.push([key, value]);
        else blocks.push([key, value]);
    }
    return (
        <div className="space-y-3">
            {rows.length > 0 && (
                <Card title="Values">
                    {rows.map(([k, v]) => <InfoRow key={k} label={k} value={String(v)} mono={typeof v === 'number'} />)}
                </Card>
            )}
            {blocks.map(([k, v]) => <ContentBlock key={k} title={k} value={v} defaultOpen={blocks.length <= 3} />)}
        </div>
    );
};

const ExternalCallCard = ({ call, index }) => {
    const info = describeExternalCall(call);
    const failed = call.error || (call.status && call.status >= 400);
    return (
        <div className={`rounded-2xl border ${failed ? 'border-red-200' : 'border-slate-200'} bg-slate-50/60 p-4 space-y-3`}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-mono text-slate-400">#{index + 1}</span>
                {call.purpose && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">{call.purpose}</span>}
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-indigo-100 text-indigo-700">
                    <Cpu className="w-3.5 h-3.5" /> {call.provider || call.service}
                </span>
                {info.model && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">{info.model}</span>}
                <span className="text-xs font-mono font-semibold text-slate-600">{call.method}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${failed ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                    {call.status ?? (call.error ? 'failed' : '—')}
                </span>
                {call.durationMs !== undefined && <span className="text-xs font-mono text-slate-500">{formatMs(call.durationMs)}</span>}
                {call.startedAt && <span className="text-xs font-mono text-slate-400 ml-auto">{formatTime(call.startedAt)}</span>}
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-1">
                <InfoRow label="Pipeline step" value={call.purpose} />
                <InfoRow label="Provider" value={call.provider || call.service} />
                <InfoRow label="API version" value={call.apiVersion} mono />
                <InfoRow label="Operation" value={call.operation} mono />
                <InfoRow label="Model (exact)" value={info.model} mono />
                <InfoRow label="Tools enabled" value={call.tools?.length ? call.tools.join(', ') : ''} mono />
                <InfoRow label="Client library" value={call.library || call.client} />
                <InfoRow label="Called by" value={formatCaller(call.caller)} mono />
                <InfoRow label="Call chain" value={(call.callChain || []).map(formatCaller).join('  ⟵  ')} mono />
                <InfoRow label="HTTP request" value={`${call.method} ${call.url}`} mono />
            </div>
            {call.error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 break-words">{call.error}</div>
            )}
            {call.settings && <ContentBlock title="Model / tool settings" value={call.settings} defaultOpen={false} tone="light" />}
            <ContentBlock title="Prompt / input sent" value={info.prompt} />
            <ContentBlock title="Model output / result" value={info.output} />
            {info.usage && <ContentBlock title="Token usage" value={info.usage} defaultOpen={false} tone="light" />}
            <ContentBlock title="Raw request body" value={call.request} defaultOpen={!info.prompt} tone="light" />
            <ContentBlock title="Raw response body" value={call.response} defaultOpen={!info.output} tone="light" />
        </div>
    );
};

const ApiCallDetail = ({ entry }) => {
    const d = entry.data || {};
    const external = Array.isArray(d.externalCalls) ? d.externalCalls : [];
    return (
        <div className="space-y-4">
            <Card title="Call">
                <InfoRow label="Backend route" value={`${d.method || ''} /api${d.endpoint || ''}`} mono />
                <InfoRow label="Frontend client" value="apiClient (axios) — frontend/src/lib/api.js" mono />
                <InfoRow label="Models used" value={summarizeModels(external)} mono />
                <InfoRow label="HTTP status" value={d.status ?? '—'} mono />
                <InfoRow label="Duration" value={formatMs(d.durationMs)} mono />
                <InfoRow label="Started" value={d.startedAt ? new Date(d.startedAt).toLocaleString() : ''} />
                <InfoRow label="Query params" value={d.params ? JSON.stringify(d.params) : ''} mono />
                <InfoRow label="External calls" value={String(external.length)} mono />
            </Card>
            {d.error && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 break-words flex gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {toText(d.error)}
                </div>
            )}
            <ContentBlock title="What went in — request sent to backend" value={d.request} defaultOpen={external.length === 0} />
            <ContentBlock title="What came back — response from backend" value={d.response} defaultOpen={external.length === 0} />
            {external.length > 0 && (
                <div className="space-y-3">
                    <h4 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <Server className="w-4 h-4 text-indigo-600" />
                        External API calls made by the backend ({external.length})
                    </h4>
                    {external.map((call, i) => <ExternalCallCard key={i} call={call} index={i} />)}
                </div>
            )}
        </div>
    );
};

const OverviewPane = ({ item, aiFields, mappingPrompt }) => {
    const { description1, description2 } = getDescriptions(item);
    const mfrs = item.classifiedManufacturers || [];
    const vdrs = item.classifiedVendors || [];
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                <Card title="Item identity">
                    <InfoRow label="Part Number" value={item.partNumber} mono />
                    <InfoRow label="Description 1" value={description1} />
                    <InfoRow label="Description 2" value={description2} />
                    <InfoRow label="Supplier Name" value={item.givenSupplierName} />
                    <InfoRow label="Supplier PN" value={item.givenSupplierPartnumber} mono />
                    <InfoRow label="Manufacturer" value={item.manufacturerName} />
                    <InfoRow label="Mfr Part #" value={item.manufacturerNumber} mono />
                    <InfoRow label="Noun/Modifier" value={item.nounModifier || '—'} />
                    <InfoRow label="Row Number" value={item.rowNumber || item.id} mono />
                </Card>
                <Card title="Processing state">
                    <InfoRow label="Pipeline step" value={item.pipelineStep || '—'} />
                    <InfoRow label="Status" value={item.status || '—'} />
                    <InfoRow label="Review status" value={item.reviewStatus || '—'} />
                    <InfoRow label="Source type" value={item.sourceType || '—'} />
                    <InfoRow label="Data source" value={item.dataSource || '—'} />
                    <InfoRow label="Search tier" value={item.searchTier !== undefined ? item.searchTier : '—'} />
                    <InfoRow label="Primary URL" value={item.sourceUrl} link />
                    <InfoRow label="Created" value={item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'} />
                    <InfoRow label="Updated" value={item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '—'} />
                </Card>
            </div>
            {(mfrs.length > 0 || vdrs.length > 0) && (
                <Card title="Manufacturer & vendor">
                    {mfrs.map((m, i) => <InfoRow key={`m${i}`} label={`Manufacturer ${i + 1}`} value={[m.name, m.partNumber && `PN ${m.partNumber}`, m.url].filter(Boolean).join(' · ')} />)}
                    {vdrs.map((v, i) => <InfoRow key={`v${i}`} label={`Vendor ${i + 1}`} value={[v.name, v.partNumber && `PN ${v.partNumber}`, v.url].filter(Boolean).join(' · ')} />)}
                </Card>
            )}
            {item.chatgptError && (
                <div className="p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{item.chatgptError}</div>
            )}
            <ContentBlock title="Final mapping prompt sent to AI" value={mappingPrompt} defaultOpen={false} />
            <ContentBlock title={`Final AI output${aiFields ? ` (${Object.keys(aiFields).length} fields)` : ''}`} value={aiFields} />
            <ContentBlock title="Held scraped content" value={item.scrapedContent} defaultOpen={false} tone="light" />
        </div>
    );
};

// ── Downloads ────────────────────────────────────────────────────────

const download = (content, filename, type) => {
    const blob = new Blob([content], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

const buildTextLog = (item, logs, identifier, aiFields) => {
    const line = '═'.repeat(90);
    const thin = '─'.repeat(90);
    const out = [`PROCESSING LOG — ${identifier}`, line,
        `Status: ${item.reviewStatus || item.status || 'N/A'} | Step: ${item.pipelineStep || 'N/A'} | Source: ${item.sourceType || 'N/A'}`,
        `URL: ${item.sourceUrl || 'N/A'} | Noun/Modifier: ${item.nounModifier || 'N/A'}`, line];
    for (const entry of logs) {
        const cfg = stageOf(entry);
        out.push('', `${cfg.icon} ${cfg.label} [${(entry.result || '').toUpperCase()}] @ ${formatTime(entry.timestamp)}`, `  ${entry.action}`);
        const d = entry.data || {};
        if (entry.stage === 'API') {
            out.push(`  status ${d.status ?? '—'} · ${formatMs(d.durationMs)}`);
            if (d.error) out.push(`  ERROR: ${toText(d.error)}`);
            out.push(`  ── REQUEST ──`, toText(d.request ?? null), `  ── RESPONSE ──`, toText(d.response ?? null));
            (d.externalCalls || []).forEach((call, i) => {
                const info = describeExternalCall(call);
                out.push(thin,
                    `  EXTERNAL #${i + 1}: ${call.purpose ? `[${call.purpose}] ` : ''}${call.provider || call.service} → ${call.status ?? call.error ?? '—'} · ${formatMs(call.durationMs)}`,
                    `    API version : ${call.apiVersion || '—'}`,
                    `    Operation   : ${call.operation || '—'}`,
                    `    Model       : ${info.model || '—'}`,
                    `    Tools       : ${call.tools?.length ? call.tools.join(', ') : '—'}`,
                    `    Library     : ${call.library || call.client || '—'}`,
                    `    Called by   : ${formatCaller(call.caller) || '—'}`,
                    `    Call chain  : ${(call.callChain || []).map(formatCaller).join(' <- ') || '—'}`,
                    `    HTTP        : ${call.method} ${call.url}`);
                if (call.settings) out.push('  ── SETTINGS ──', toText(call.settings));
                if (info.prompt) out.push('  ── PROMPT ──', info.prompt);
                if (info.output) out.push('  ── OUTPUT ──', info.output);
                if (!info.prompt) out.push('  ── RAW REQUEST ──', toText(call.request ?? null));
                if (!info.output) out.push('  ── RAW RESPONSE ──', toText(call.response ?? null));
            });
        } else {
            for (const [k, v] of Object.entries(d)) {
                if (v === null || v === undefined || v === '') continue;
                out.push(typeof v === 'object' || String(v).includes('\n') ? `  ${k}:\n${toText(v)}` : `  ${k}: ${v}`);
            }
        }
    }
    if (aiFields) out.push('', line, 'FINAL AI OUTPUT', toText(aiFields));
    return out.join('\n');
};

// ── Main component ───────────────────────────────────────────────────

function ProcessingLogModal({ item, onClose }) {
    const [selected, setSelected] = useState('overview');
    const [filter, setFilter] = useState('all');
    const [query, setQuery] = useState('');

    // A newly opened item starts on the overview
    useEffect(() => {
        setSelected('overview');
    }, [item?.id]);

    useEffect(() => {
        if (!item) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [item, onClose]);

    const logs = useMemo(() => (Array.isArray(item?.processingLog) ? item.processingLog : []), [item]);

    const aiFields = useMemo(() => {
        if (!item?.chatgptOutput) return null;
        try { return typeof item.chatgptOutput === 'string' ? JSON.parse(item.chatgptOutput) : item.chatgptOutput; }
        catch { return item.chatgptOutput; }
    }, [item]);

    const mappingPrompt = useMemo(() => {
        if (item?.chatgptPrompt) return item.chatgptPrompt;
        const withPrompt = [...logs].reverse().find(e => (e.stage === 'EXTRACT' || e.stage === 'PROMPT') && e.data?.fullPrompt);
        return withPrompt?.data.fullPrompt || null;
    }, [item, logs]);

    // Timeline with each API call attached to the step that preceded it
    const timeline = useMemo(() => {
        let currentStep = null;
        return logs.map((entry, index) => {
            const isApi = entry.stage === 'API';
            if (!isApi) currentStep = entry.stage;
            return { entry, index, isApi, parentStage: isApi ? currentStep : null };
        });
    }, [logs]);

    const stats = useMemo(() => {
        const api = timeline.filter(t => t.isApi);
        return {
            steps: timeline.length - api.length,
            api: api.length,
            external: api.reduce((n, t) => n + (t.entry.data?.externalCalls?.length || 0), 0),
            errors: timeline.filter(t => t.entry.result === 'error').length,
        };
    }, [timeline]);

    const visible = useMemo(() => {
        const q = query.trim().toLowerCase();
        return timeline.filter(t => {
            if (filter === 'steps' && t.isApi) return false;
            if (filter === 'api' && !t.isApi) return false;
            if (filter === 'errors' && t.entry.result !== 'error') return false;
            return entryMatches(t.entry, q);
        });
    }, [timeline, filter, query]);

    if (!item) return null;

    const identifier = item.partNumber || item.givenSupplierPartnumber || item.manufacturerNumber || `Row #${item.rowNumber || item.id}`;
    const status = item.reviewStatus || item.status || 'PENDING';
    const selectedItem = selected === 'overview' ? null : timeline.find(t => t.index === selected);
    const fileBase = `Log_${String(identifier).replace(/[^\w.-]+/g, '_')}_${new Date().toISOString().slice(0, 10)}`;

    return (
        <div className="fixed inset-0 z-[70] bg-slate-100 flex flex-col animate-fade-in" role="dialog" aria-modal="true">
            {/* Header */}
            <header className="shrink-0 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
                    <ScrollText className="w-5 h-5 text-indigo-600" />
                </div>
                <div className="min-w-0 flex-1">
                    <h2 className="text-lg font-bold text-slate-900 truncate">Processing Log — {identifier}</h2>
                    <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs">
                        <span className={`px-2 py-0.5 rounded-full font-bold ${STATUS_COLORS[status] || STATUS_COLORS.PENDING}`}>{status}</span>
                        <span className="text-slate-500">{stats.steps} steps</span>
                        <span className="text-slate-500">· {stats.api} API calls</span>
                        <span className="text-slate-500">· {stats.external} external calls</span>
                        {stats.errors > 0 && <span className="text-red-600 font-semibold">· {stats.errors} errors</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => download(buildTextLog(item, logs, identifier, aiFields), `${fileBase}.txt`, 'text/plain;charset=utf-8')}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
                        <FileText className="w-4 h-4" /> TXT
                    </button>
                    <button onClick={() => download(JSON.stringify(item, null, 2), `${fileBase}.json`, 'application/json')}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
                        <FileJson className="w-4 h-4" /> JSON
                    </button>
                    <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100" title="Close (Esc)">
                        <X className="w-5 h-5 text-slate-600" />
                    </button>
                </div>
            </header>

            <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
                {/* Timeline */}
                <aside className="lg:w-[400px] shrink-0 bg-white border-b lg:border-b-0 lg:border-r border-slate-200 flex flex-col max-h-[40vh] lg:max-h-none">
                    <div className="p-3 space-y-2 border-b border-slate-100">
                        <div className="relative">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search prompts, responses, URLs…"
                                className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
                        </div>
                        <div className="flex gap-1">
                            {FILTERS.map(f => (
                                <button key={f.key} onClick={() => setFilter(f.key)}
                                    className={`flex-1 px-2 py-1.5 rounded-lg text-xs font-semibold ${filter === f.key ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <nav className="flex-1 overflow-y-auto p-2 space-y-1">
                        <button onClick={() => setSelected('overview')}
                            className={`w-full text-left px-3 py-2.5 rounded-xl text-sm font-semibold ${selected === 'overview' ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' : 'text-slate-700 hover:bg-slate-50'}`}>
                            📋 Overview — item, final prompt & output
                        </button>
                        {visible.length === 0 && (
                            <p className="text-center text-sm text-slate-400 py-6">
                                {logs.length === 0 ? 'No processing log recorded for this item.' : 'No entries match.'}
                            </p>
                        )}
                        {visible.map(({ entry, index, isApi }) => {
                            const cfg = stageOf(entry);
                            const active = selected === index;
                            const d = entry.data || {};
                            return (
                                <button key={index} onClick={() => setSelected(index)}
                                    className={`w-full text-left rounded-xl px-3 py-2 ${isApi ? 'ml-4 w-[calc(100%-1rem)]' : ''} ${active ? 'bg-indigo-50 ring-1 ring-indigo-200' : 'hover:bg-slate-50'}`}>
                                    <div className="flex items-center gap-2">
                                        <span className={`w-2 h-2 rounded-full shrink-0 ${RESULT_DOT[entry.result] || RESULT_DOT.info}`} />
                                        <span className="text-sm">{cfg.icon}</span>
                                        <span className={`text-xs font-bold uppercase tracking-wide truncate ${isApi ? 'text-indigo-600' : 'text-slate-700'}`}>
                                            {isApi ? `${d.method || ''} ${d.endpoint || ''}` : cfg.label}
                                        </span>
                                        <span className="ml-auto text-[10px] font-mono text-slate-400 shrink-0">{formatTime(entry.timestamp)}</span>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-0.5 truncate pl-4">
                                        {isApi
                                            ? [d.status ?? '—', formatMs(d.durationMs), summarizeModels(d.externalCalls) || null].filter(Boolean).join(' · ')
                                            : entry.action}
                                    </p>
                                </button>
                            );
                        })}
                    </nav>
                </aside>

                {/* Detail */}
                <main className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6">
                    <div className="max-w-6xl mx-auto">
                        {selected === 'overview' || !selectedItem ? (
                            <OverviewPane item={item} aiFields={aiFields} mappingPrompt={mappingPrompt} />
                        ) : (
                            <div className="space-y-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-2xl">{stageOf(selectedItem.entry).icon}</span>
                                    <h3 className="text-lg font-bold text-slate-900">
                                        {selectedItem.isApi ? 'API Call' : stageOf(selectedItem.entry).label}
                                    </h3>
                                    <span className="px-2 py-0.5 rounded-md text-[11px] font-mono font-semibold bg-slate-200 text-slate-700">
                                        {selectedItem.entry.stage}
                                    </span>
                                    {selectedItem.isApi && selectedItem.parentStage && (
                                        <span className="text-xs text-slate-500">during {stageOf({ stage: selectedItem.parentStage }).label}</span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${RESULT_BADGE[selectedItem.entry.result] || RESULT_BADGE.info}`}>
                                        {(selectedItem.entry.result || 'info').toUpperCase()}
                                    </span>
                                    <span className="ml-auto text-xs font-mono text-slate-400">
                                        {selectedItem.entry.timestamp ? new Date(selectedItem.entry.timestamp).toLocaleString() : ''}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-700 font-medium break-words">{selectedItem.entry.action}</p>
                                {selectedItem.isApi
                                    ? <ApiCallDetail entry={selectedItem.entry} />
                                    : <DataView data={selectedItem.entry.data} />}
                            </div>
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
}

export default ProcessingLogModal;
